import { getApiHost } from 'base/api_host';
import { exists } from 'base/helpers';
import { generateRequestId, ServiceHttpError } from 'base/service_error';
import { zodParse } from 'base/zod_parse';
import { z } from 'zod';
import { globalPromptOverridesStore } from './editor/prompt_overrides';
import { FeatureFlagsStore } from './flag_overrides';

export interface LLMProxyService {
  /** Streams the completion of a prompt */
  infer(
    prompt: string,
    promptContext: Record<string, any>,
    opts?: CompletionsOpts,
  ): AsyncIterable<string, CompletionsResponse>;

  postFeedback(
    score: -1 | 1,
    comment: string,
    usageIds: readonly number[],
  ): Promise<void>;
}

/** TypeScript's builtin doesn't specify TReturn. */
interface AsyncIterable<T, TReturn = unknown> {
  [Symbol.asyncIterator](): AsyncIterator<T, TReturn>;
}

type CompletionsResponse = {
  finishReason: string;
};

export type CompletionsOpts = {
  abortSignal?: AbortSignal;
  onRetry?(): void;
  onGetUsageId?(usageId: number): void;
};

export const ASYNC_GENERATOR_ERROR_SYMBOL = Symbol('async generator error');

export class LLMUserFacingError extends Error {
  constructor(readonly userMessage: string) {
    super(userMessage);
  }
}

export class HttpLLMProxyService implements LLMProxyService {
  constructor(
    private readonly getToken: () => Promise<string> | string,
    private readonly flags: FeatureFlagsStore,
    private readonly blocksProjectId?: string,
  ) {}

  withBlocksProjectId(blocksProjectId: string): HttpLLMProxyService {
    return new HttpLLMProxyService(this.getToken, this.flags, blocksProjectId);
  }

  async *infer(
    prompt: string,
    promptContext: Record<string, any>,
    opts?: CompletionsOpts,
  ): AsyncIterable<string, CompletionsResponse> {
    try {
      const promptOverride = this.flags.get('enablePromptOverrides')
        ? globalPromptOverridesStore.get(prompt)
        : undefined;

      const requestId = generateRequestId();
      const params = new URLSearchParams({
        member_token: await this.getToken(),
      });
      const url = `${getApiHost()}llm/v1/infer?${params}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: [['x-request-id', requestId]],
        body: JSON.stringify({
          prompt,
          promptContext,
          blocksProjectId: this.blocksProjectId,
          ...(promptOverride
            ? {
                promptOverride: promptOverride.template,
                modelOverride: promptOverride.model ?? null,
                temperatureOverride: promptOverride.temperature ?? null,
              }
            : {}),
        }),
        signal: opts?.abortSignal,
      });
      if (!res.ok) {
        const text = await res.text();
        throw new ServiceHttpError(res.status, text, requestId);
      }
      if (!res.body) throw new Error('no response body');

      const abortPromise = new Promise<never>((_, reject) => {
        if (!opts?.abortSignal) return;
        opts.abortSignal.addEventListener(
          'abort',
          () => {
            reject(new Error('aborted'));
          },
          { once: true },
        );
      });

      const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
      let buffer = '';
      let usageId: number | undefined;
      while (true) {
        const { value, done } = await Promise.race([
          reader.read(),
          abortPromise,
        ]);
        const parseResult = parseEvents(buffer + value);
        buffer = parseResult.rest;
        for (const eventString of parseResult.events) {
          const eventRaw = JSON.parse(eventString);
          const event = zodParse(inferEventSchema, eventRaw);
          if (event.error) {
            throw new LLMUserFacingError(event.error);
          }
          if (event.content) {
            yield event.content;
          }
          if (event.usageId && usageId == null) {
            usageId = event.usageId;
            opts?.onGetUsageId?.(usageId);
            console.info(
              `Streaming a ${prompt} completion:`,
              `${getApiHost()}admin/llm_proxy/usage/${usageId}/change/`,
            );
          }
          if (event.finishReason) {
            return { finishReason: event.finishReason };
          }
          if (event.serverRetryCount) {
            opts?.onRetry?.();
          }
        }
        if (done) {
          throw new Error(
            `End of body before error/finish_reason.  Remaining buffer: ${buffer}`,
          );
        }
      }
    } catch (e) {
      // The interactions between async generators and unhandledrejection
      // confuse me.  Let's just tag the errors and make sentry ignore them.
      (e as any)[ASYNC_GENERATOR_ERROR_SYMBOL] = true;
      throw e;
    }
  }

  async postFeedback(
    score: -1 | 1,
    comment: string,
    usageIds: readonly number[],
  ): Promise<void> {
    const requestId = generateRequestId();
    const params = new URLSearchParams({
      member_token: await this.getToken(),
    });
    const url = `${getApiHost()}llm/v1/feedback?${params}`;
    const res = await fetch(url, {
      method: 'POST',
      headers: [['x-request-id', requestId]],
      body: JSON.stringify({
        score,
        comment,
        usageIds,
      }),
    });
    if (!res.ok) {
      throw new ServiceHttpError(res.status, await res.text(), requestId);
    }
  }
}

const inferEventSchema = z.object({
  content: z.string().optional(),
  error: z.string().optional(),
  finishReason: z.string().optional(),
  usageId: z.number().optional(),
  serverRetryCount: z.number().optional(),
});

/** Parses server side events from OpenAI or our Django.  Not the full spec */
function parseEvents(buffer: string): {
  events: string[];
  rest: string;
} {
  const lines = buffer.split('\n');
  const rest = lines.pop() || '';
  const events = lines
    .map((l) => {
      l = l.trim();
      if (l.length == 0) return;
      if (l.startsWith('data: ')) {
        return l.substring(6);
      }
      throw new Error(`unknown SSE line: ${l}`);
    })
    .filter(exists);
  return { events, rest };
}
